import type { VariantProps } from 'class-variance-authority';
import type { Button, buttonVariants } from 'components/ui/button';
import { FOCUS_RING } from 'layout/NewDesignLayout/focusRing';
import type { ComponentProps, ReactNode } from 'react';
import { cn } from 'utils/cn';

/**
 * The prop vocabulary the app's button call sites are written in — `variant="contained"`,
 * `color="secondary"`, `size="small"` — resolved onto the kit button's own variants.
 */
export type ButtonStyleProps = {
    variant?: 'contained' | 'outlined' | 'text';
    color?: 'primary' | 'secondary' | 'error' | 'info';
    /** `small` is the app's 40px control height; `extraSmall` is the compact in-row button. */
    size?: 'small' | 'extraSmall';
};

/** A kit button taking {@link ButtonStyleProps}, with icons either side of the label. */
export type AppButtonProps = Omit<ComponentProps<typeof Button>, 'variant' | 'size' | 'className'> &
    ButtonStyleProps & {
        className?: string;
        startIcon?: ReactNode;
        endIcon?: ReactNode;
    };

type KitButtonVariants = VariantProps<typeof buttonVariants>;

/**
 * The kit button pins every unsized SVG inside it to 16px. The app's glyphs size themselves in `em`
 * off their own `fontSize`, and call sites size them with text utilities, so `1em` hands the size
 * back to the glyph instead.
 */
export const GLYPH_OWN_SIZE = "[&_svg:not([class*='size-'])]:size-[1em]";

/**
 * The wrapper for a glyph beside a button's label. It sets the font size of its first child, which the
 * glyph's `1em` then resolves against: 18px for `small`, 20px for `medium` (a call site that passes no
 * `size`), and nothing for `extraSmall`, where the glyph keeps its own size. An explicit `size-*` on the
 * glyph still wins. `contents` keeps the wrapper out of the button's flex row.
 */
export const ICON_SLOT = {
    small: 'contents [&>*:nth-of-type(1)]:text-[18px]',
    medium: 'contents [&>*:nth-of-type(1)]:text-[20px]',
    extraSmall: 'contents',
} as const;

/**
 * A button that is off yet stays a Tab stop (`focusableWhenDisabled`, which makes it `aria-disabled`): the default
 * cursor and no press nudge. Its fill and its label are dimmed by the classes built on this one, not by the
 * button's opacity, which would dim its focus ring too.
 */
export const OFF_ACTION_CLASS = 'aria-disabled:cursor-default aria-disabled:active:translate-y-0';

/**
 * A glyph-only button inside or beside a field's label: a 24px round target, a shade darker than the hint icons so
 * it reaches 3:1. Its glyph is `h-5 w-auto`.
 */
export const FIELD_GLYPH_BUTTON_CLASS = cn(
    'text-grey-500 new:text-muted-foreground hover:text-grey-600 new:hover:text-foreground-secondary inline-flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-full',
    FOCUS_RING
);

/** {@link OFF_ACTION_CLASS} for an outlined button: its fill and label dimmed. */
export const OFF_OUTLINED_CLASS = cn(
    OFF_ACTION_CLASS,
    'aria-disabled:bg-card/50 aria-disabled:text-foreground/50 aria-disabled:hover:bg-card/50 aria-disabled:hover:text-foreground/50'
);

/** {@link OFF_ACTION_CLASS} for a button on the secondary fill. */
export const OFF_SECONDARY_CLASS = cn(
    OFF_ACTION_CLASS,
    'aria-disabled:bg-secondary/50 aria-disabled:text-secondary-foreground/50 aria-disabled:hover:bg-secondary/50'
);

export function buttonStyle(
    { variant = 'text', color = 'primary', size = 'small' }: ButtonStyleProps,
    className?: string
): { variant: KitButtonVariants['variant']; size: KitButtonVariants['size']; className: string } {
    const kitSize = size === 'extraSmall' ? 'xs' : 'lg';
    const withGlyphs = (colorClass?: string | false) => cn(GLYPH_OWN_SIZE, colorClass, className);

    if (variant === 'outlined') {
        // The kit outline sets no resting text colour, so without one the label takes its container's —
        // the page header's green.
        return {
            variant: 'outline',
            size: kitSize,
            className: withGlyphs(color === 'error' ? 'text-destructive' : 'text-grey-900'),
        };
    }
    if (variant === 'text') {
        return {
            variant: 'link',
            size: kitSize,
            className: withGlyphs(color === 'error' ? 'text-destructive' : 'text-link'),
        };
    }
    switch (color) {
        case 'secondary':
            return { variant: 'secondary', size: kitSize, className: withGlyphs() };
        case 'error':
            return { variant: 'destructive', size: kitSize, className: withGlyphs() };
        case 'info':
            return { variant: 'default', size: kitSize, className: withGlyphs('bg-info hover:bg-info/80') };
        case 'primary':
        default:
            return { variant: 'default', size: kitSize, className: withGlyphs() };
    }
}
